import { appLog } from '@shared/common/logging/app-log.util';
import { PlatformLocation, registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, DEFAULT_CURRENCY_CODE, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { AppConsts } from '@shared/AppConsts';
import { INTIMECommonModule } from '@shared/common/common.module';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { UserScopedState } from '@shared/common/session/user-scoped-state';
import { AppUiCustomizationService } from '@shared/common/ui/app-ui-customization.service';
import { UrlHelper } from '@shared/helpers/UrlHelper';
import { ThemeModeHelper } from '@shared/helpers/ThemeModeHelper';
import { API_BASE_URL, UiCustomizationSettingsDto, ThemeSettingsDto, ThemeMenuSettingsDto, ThemeLayoutSettingsDto, ThemeHeaderSettingsDto, ThemeSubHeaderSettingsDto, ThemeFooterSettingsDto, ApplicationInfoDto } from '@shared/service-proxies/service-proxies';
import { ServiceProxyModule } from '@shared/service-proxies/service-proxy.module';
import * as localForage from 'localforage';
import * as _ from 'lodash';
import moment from 'moment';
import { AppPreBootstrap } from './AppPreBootstrap';
import { AppModule } from './app/app.module';
import { RootRoutingModule } from './root-routing.module';
import { RootComponent } from './root.component';
import { DomHelper } from '@shared/helpers/DomHelper';
import { CookieConsentService } from '@shared/common/session/cookie-consent.service';
import { NgxBootstrapDatePickerConfigService } from '@assets/ngx-bootstrap/ngx-bootstrap-datepicker-config.service';
import { LocaleMappingService } from '@shared/locale-mapping.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { DateTimeService } from '@app/shared/common/timing/date-time.service';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

/** Aura dark form fields default to near-black (surface.950); align with ITP dark surfaces. */
const IntimeAura = definePreset(Aura, {
    semantic: {
        colorScheme: {
            dark: {
                formField: {
                    background: '#262626',
                    disabledBackground: '#303030',
                    filledBackground: '#2b2b2b',
                    filledHoverBackground: '#2b2b2b',
                    filledFocusBackground: '#2b2b2b',
                    borderColor: '#3d3d3d',
                    hoverBorderColor: '#5a5a5a',
                },
            },
        },
    },
    components: {
        checkbox: {
            root: {
                background: '{form.field.background}',
                borderColor: '{form.field.border.color}',
                hoverBorderColor: '{form.field.hover.border.color}',
                checkedBackground: '{primary.color}',
                checkedBorderColor: '{primary.color}',
                checkedHoverBackground: '{primary.hover.color}',
                checkedHoverBorderColor: '{primary.hover.color}',
            },
        },
    },
});
export function appInitializerFactory(injector: Injector, platformLocation: PlatformLocation) {
    return () => {
        let spinnerService = injector.get(NgxSpinnerService);
        const isSessionLocked = location.href.indexOf('/account/session-locked') >= 0;

        if (isSessionLocked) {
            // Hide the static root-initial-loader immediately — Angular won't render
            // until APP_INITIALIZER resolves, so we remove it from the DOM right away.
            const initialLoader = document.querySelector('.root-initial-loader');
            if (initialLoader) {
                (initialLoader as HTMLElement).style.display = 'none';
            }
        } else {
            spinnerService.show();
        }

        return new Promise<boolean>((resolve, reject) => {
            AppConsts.appBaseHref = getBaseHref(platformLocation);
            let appBaseUrl = getDocumentOrigin() + AppConsts.appBaseHref;

            AppPreBootstrap.run(
                appBaseUrl,
                injector,
                () => {
                    handleLogoutRequest(injector.get(AppAuthService));

                    if (UrlHelper.isInstallUrl(location.href)) {
                        doConfigurationForInstallPage(injector);
                        spinnerService.hide();
                        resolve(true);
                    } else if (isSessionLocked) {
                        // Skip appSessionService.init() (getCurrentLoginInformations API call)
                        // on the session-locked page — token is already cleared, user session
                        // is not needed, and the extra round-trip causes an indefinite loader.
                        doConfigurationForInstallPage(injector);
                        initializeCookieConsent(injector);
                        spinnerService.hide();
                        resolve(true);
                    } else {
                        let appSessionService: AppSessionService = injector.get(AppSessionService);
                        appSessionService.init().then(
                            (result) => {
                                // Runs before any component is created, so a user switch on this
                                // browser starts with the previous user's cached profile/state
                                // already gone and every reader fetches fresh data.
                                UserScopedState.claimFor(appSessionService.userId, appSessionService.tenantId);
                                initializeAppCssClasses(injector, result);
                                initializeTenantResources(injector);
                                initializeCookieConsent(injector);
                                registerLocales(resolve, reject, spinnerService);
                            },
                            (err) => {
                                spinnerService.hide();
                                reject(err);
                            }
                        );
                    }
                },
                resolve,
                reject
            );
        });
    };
}
function initializeLocalForage() {
    localForage.config({
        driver: localForage.LOCALSTORAGE,
        name: 'INTIME',
        version: 1.0,
        storeName: 'abpzerotemplate_local_storage',
        description: 'Cached data for INTIME'
    });
}
function getDefaultThemeForInstallPage(): UiCustomizationSettingsDto {
    let theme = new UiCustomizationSettingsDto();
    theme.baseSettings = new ThemeSettingsDto();
    theme.baseSettings.theme = 'default';
    theme.baseSettings.menu = new ThemeMenuSettingsDto();
    theme.baseSettings.menu.asideSkin = 'light';
    theme.baseSettings.header = new ThemeHeaderSettingsDto();
    theme.baseSettings.header.headerSkin = 'light';
    theme.baseSettings.subHeader = new ThemeSubHeaderSettingsDto();
    theme.baseSettings.layout = new ThemeLayoutSettingsDto();
    theme.baseSettings.layout.layoutType = 'fluid';
    theme.baseSettings.header = new ThemeHeaderSettingsDto();
    theme.baseSettings.footer = new ThemeFooterSettingsDto();
    return theme;
}
function setApplicationInfoForInstallPage(injector, theme: UiCustomizationSettingsDto) {
    let appSessionService: AppSessionService = injector.get(AppSessionService);
    appSessionService.theme = theme;
    appSessionService.application = new ApplicationInfoDto();
    appSessionService.application.releaseDate = (moment().startOf('day') as any).format('YYYY-MM-DDTHH:mm:ss');
}
function doConfigurationForInstallPage(injector) {
    let theme = getDefaultThemeForInstallPage();
    setApplicationInfoForInstallPage(injector, theme);
    initializeAppCssClasses(injector, theme);
}
function initializeAppCssClasses(injector: Injector, theme: UiCustomizationSettingsDto) {
    let appUiCustomizationService = injector.get(AppUiCustomizationService);
    appUiCustomizationService.init(theme);
    //Css classes based on the layout
    if (abp.session.userId) {
        document.body.className = appUiCustomizationService.getAppModuleBodyClass();
    }
    else {
        document.body.className = appUiCustomizationService.getAccountModuleBodyClass();
    }
    ThemeModeHelper.applyThemeMode();
}
function initializeTenantResources(injector: Injector) {
    let appSessionService: AppSessionService = injector.get(AppSessionService);
    if (appSessionService.tenant && appSessionService.tenant.customCssId) {
        document.head.appendChild(DomHelper.createElement('link', [
            {
                key: 'id',
                value: 'TenantCustomCss'
            },
            {
                key: 'rel',
                value: 'stylesheet'
            },
            {
                key: 'href',
                value: AppConsts.remoteServiceBaseUrl + '/TenantCustomization/GetCustomCss?tenantId=' + appSessionService.tenant.id
            }
        ]));
    }
    let metaImage = DomHelper.getElementByAttributeValue('meta', 'property', 'og:image');
    if (metaImage) {
        //set og share image meta tag
        //if (!appSessionService.tenant || !appSessionService.tenant.logoId) {
        if (!appSessionService.tenant) {
            let ui: AppUiCustomizationService = injector.get(AppUiCustomizationService);
            metaImage.setAttribute('content', window.location.origin + '/assets/common/images/app-logo-on-' + abp.setting.get(appSessionService.theme.baseSettings.theme + '.' + 'App.UiManagement.Left.AsideSkin') + '.svg');
        }
        else {
            metaImage.setAttribute('content', AppConsts.remoteServiceBaseUrl + '/TenantCustomization/GetLogo?tenantId=' + appSessionService.tenant.id);
        }
    }
}
function initializeCookieConsent(injector: Injector) {
    let cookieConsentService: CookieConsentService = injector.get(CookieConsentService);
    cookieConsentService.init();
}
function getDocumentOrigin() {
    if (!document.location.origin) {
        return document.location.protocol + '//' + document.location.hostname + (document.location.port ? ':' + document.location.port : '');
    }
    return document.location.origin;
}
function normalizeAngularLocaleDataId(angularLocale: string): string {
    // @angular/common/locales ships `en.js` but not `en-US.js`; map common BCP-47 tags to existing bundles.
    if (angularLocale === 'en-US' || angularLocale === 'en_US') {
        return 'en';
    }
    return angularLocale;
}
/** Locale filenames under @angular/common/locales — only [a-zA-Z0-9-] (no path segments). */
function assertSafeLocaleFilename(id: string): string {
    if (!/^[a-zA-Z0-9-]+$/.test(id)) {
        throw new Error(`Invalid Angular locale id: ${id}`);
    }
    return id;
}
function registerLocales(resolve: (value?: boolean | Promise<boolean>) => void, reject: any, spinnerService: NgxSpinnerService) {
    if (shouldLoadLocale()) {
        const raw = convertAbpLocaleToAngularLocale(abp.localization.currentLanguage.name);
        const angularLocale = assertSafeLocaleFilename(normalizeAngularLocaleDataId(raw));
        // Dynamic imports must target a real file path: `@angular/common/locales/*` is exported but
        // `import('@angular/common/locales/' + id)` still resolves to `./locales` and fails package "exports".
        import(/* webpackChunkName: "ng-locale-[request]" */ `../node_modules/@angular/common/locales/${angularLocale}.js`)
            .then(module => {
            registerLocaleData(module.default);
            NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales().then(_ => {
                resolve(true);
                spinnerService.hide();
            });
        })
            .catch(err => {
            appLog.warn(`Failed to load Angular locale data for "${angularLocale}" (mapped from "${raw}")`, err);
            NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales().then(_ => {
                resolve(true);
                spinnerService.hide();
            });
        });
    }
    else {
        NgxBootstrapDatePickerConfigService.registerNgxBootstrapDatePickerLocales().then(_ => {
            resolve(true);
            spinnerService.hide();
        });
    }
}
export function shouldLoadLocale(): boolean {
    return abp.localization.currentLanguage.name && abp.localization.currentLanguage.name !== 'en-US';
}
export function convertAbpLocaleToAngularLocale(locale: string): string {
    return new LocaleMappingService().map('angular', locale);
}
export function getRemoteServiceBaseUrl(): string {
    return AppConsts.remoteServiceBaseUrl;
}
export function getCurrentLanguage(): string {
    return convertAbpLocaleToAngularLocale(abp.localization.currentLanguage.name);
}
export function getCurrencyCode(injector: Injector): string {
    let appSessionService: AppSessionService = injector.get(AppSessionService);
    return appSessionService.application.currency;
}
export function getBaseHref(platformLocation: PlatformLocation): string {
    let baseUrl = platformLocation.getBaseHrefFromDOM();
    if (baseUrl) {
        return baseUrl;
    }
    return '/';
}
function handleLogoutRequest(authService: AppAuthService) {
    let currentUrl = UrlHelper.initialUrl;
    let returnUrl = UrlHelper.getReturnUrl();
    if (currentUrl.indexOf(('account/logout')) >= 0 && returnUrl) {
        authService.logout(true, returnUrl);
    }
}
@NgModule({
    declarations: [RootComponent],
    bootstrap: [RootComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        RootRoutingModule,
        AppModule,
        INTIMECommonModule.forRoot(),
        ServiceProxyModule,
        NgxSpinnerModule,
        NgbModule
    ],
    providers: [
        { provide: API_BASE_URL, useFactory: getRemoteServiceBaseUrl },
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFactory,
            deps: [Injector, PlatformLocation],
            multi: true
        },
        {
            provide: LOCALE_ID,
            useFactory: getCurrentLanguage
        },
        {
            provide: DEFAULT_CURRENCY_CODE,
            useFactory: getCurrencyCode,
            deps: [Injector]
        },
        providePrimeNG({
            theme: {
                preset: IntimeAura,
                options: {
                    darkModeSelector: `[${ThemeModeHelper.attributeName}="dark"]`
                }
            }
        }),
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class RootModule {
}
